import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Card, Image, LoadingOverlay, Stack, Title } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import nextLogo from "@/assets/logo.png";
import { Input } from "@/components/@custom/Input";
import { SideBySide } from "@/components/@custom/SideBySide";
import { useRecaptcha } from "@/hooks";
import { useWindowLocationHost } from "@/hooks/useWindowLocationHost";
import { GLOBAL_SCHEMA } from "@/lib/zod";
import { useAuth, useGetCompanyByHostQuery } from "@/store/apis/nextcargo";

// Forçar uso do domínio recaptcha.net
if (typeof window !== "undefined") {
  window.recaptchaOptions = { useRecaptchaNet: true };
}

type Schema = z.infer<typeof schema>;
const schema = z.object({
  email: GLOBAL_SCHEMA.EMAIL,
  password: z.string().min(4, { message: "Senha deve ter pelo menos 4 caracteres" })
});

export const Route = createFileRoute("/_auth/expedicao/login")({
  component: ExpeditionLogin,
  head: () => ({
    meta: [{ title: "Login - Expedição" }]
  }),
  pendingComponent: () => <LoadingOverlay visible loaderProps={{ type: "bars", size: "xl", color: "primary" }} overlayProps={{ backgroundOpacity: 1 }} />
});
function ExpeditionLogin() {
  useRecaptcha();
  const { windowLocationHost, subDomain } = useWindowLocationHost();
  const { data: company } = useGetCompanyByHostQuery({ host: windowLocationHost }, { skip: !subDomain });

  const { SignIn } = useAuth();

  const {
    control,
    handleSubmit: formSubmit,
    formState: { isSubmitting }
  } = useForm<Schema>({
    mode: "all",
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
      password: ""
    }
  });

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const { email, password } = data;

    await SignIn("expedition")({ email, password, company: { id: Number(company?.id) }, recaptcha_token: import.meta.env.VITE_RECAPTCHA_KEY });
  };

  /* ############################################################ */

  const logoSrc = {
    logo: `${import.meta.env.VITE_APP_API_URL}/${company?.logo}`,
    fallback: nextLogo
  };

  return (
    <>
      {company && (
        <>
          <SideBySide.Primary>
            <Image src={logoSrc.logo} fallbackSrc={logoSrc.fallback} className={`mb-2 block w-[300px]! md:hidden!`} />

            <Card shadow="lg" radius="lg" p="xl" withBorder>
              <Stack>
                <Title order={1} c="gray.8">
                  Entrar na sua conta
                </Title>

                <form onSubmit={formSubmit(onSubmit)} className="flex flex-col gap-4">
                  <div className="flex flex-col gap-4">
                    <Input.Text label="Email" description="Digite seu email" control={control} name="email" placeholder="Email" withAsterisk />
                    <Input.Password label="Senha" description="Digite sua senha" control={control} type="password" name="password" placeholder="Senha" withAsterisk />
                  </div>

                  <Button
                    type="submit"
                    className="bg-primary w-full cursor-pointer rounded py-2 font-semibold text-white transition hover:brightness-110 disabled:opacity-50"
                    loading={isSubmitting}
                  >
                    Entrar
                  </Button>
                </form>
              </Stack>
            </Card>
          </SideBySide.Primary>

          <SideBySide.Secondary>
            <Image src={logoSrc.logo} fallbackSrc={logoSrc.fallback} className={`w-[400px]! xl:w-[550px]!`} />
          </SideBySide.Secondary>
        </>
      )}
    </>
  );
}
